import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  Plus, Package, Search, RefreshCw, Eye, FileText, CheckCircle, 
  XCircle, Clock, Truck, CreditCard, Building2, ChevronDown,
  Receipt, IndianRupee, Calendar, Filter, Download, AlertCircle
} from 'lucide-react';

const ProductPurchase = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Form state for new PO
  const [newPO, setNewPO] = useState({
    supplier_id: '',
    bill_type: 'GST_Invoice',
    items: [],
    supplier_invoice_number: '',
    supplier_invoice_date: '',
    expected_delivery_date: '',
    notes: ''
  });

  // Payment form state
  const [payment, setPayment] = useState({
    payment_mode: 'UPI',
    amount: 0,
    transaction_id: '',
    receipt_number: '',
    cheque_number: '',
    bank_name: '',
    reference_notes: ''
  });

  // Item being added
  const [newItem, setNewItem] = useState({
    product_id: '',
    sku_code: '',
    product_name: '',
    brand: '',
    category: '',
    variant: '',
    quantity: 1,
    unit_price: 0,
    gst_rate: 18
  });

  useEffect(() => {
    fetchDashboard();
    fetchSuppliers();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchPurchaseOrders();
    }
  }, [activeTab, statusFilter]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-purchase/dashboard`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      const data = await response.json();
      setDashboard(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const url = statusFilter === 'all' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/product-purchase/pos`
        : `${process.env.REACT_APP_BACKEND_URL}/api/product-purchase/pos?status=${statusFilter}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setPurchaseOrders(data.purchase_orders || []);
    } catch (error) {
      console.error('Error fetching POs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/suppliers`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      const data = await response.json();
      setSuppliers(data.suppliers || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products?category=Mobile Phones`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleAddItem = () => {
    if (!newItem.product_id || !newItem.quantity || !newItem.unit_price) {
      alert('Please fill all item details');
      return;
    }
    
    const gstAmount = newPO.bill_type === 'GST_Invoice' 
      ? (newItem.unit_price * newItem.quantity * newItem.gst_rate) / 100 
      : 0;
    
    setNewPO(prev => ({
      ...prev,
      items: [...prev.items, {
        ...newItem,
        gst_amount: gstAmount,
        total_amount: (newItem.unit_price * newItem.quantity) + gstAmount
      }]
    }));
    
    setNewItem({
      product_id: '',
      sku_code: '',
      product_name: '',
      brand: '',
      category: '',
      variant: '',
      quantity: 1,
      unit_price: 0,
      gst_rate: 18
    });
  };

  const handleRemoveItem = (index) => {
    setNewPO(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setNewItem(prev => ({
        ...prev,
        product_id: product.id,
        sku_code: product.sku_code,
        product_name: product.model_name,
        brand: product.brand_name,
        category: product.category
      }));
    }
  };

  const handleCreatePO = async () => {
    if (!newPO.supplier_id || newPO.items.length === 0) {
      alert('Please select supplier and add at least one item');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-purchase/po/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(newPO)
        }
      );
      const data = await response.json();
      
      if (response.ok) {
        alert(`PO Created: ${data.po_number}`);
        setShowCreateModal(false);
        setNewPO({
          supplier_id: '',
          bill_type: 'GST_Invoice',
          items: [],
          supplier_invoice_number: '',
          supplier_invoice_date: '',
          expected_delivery_date: '',
          notes: ''
        });
        fetchDashboard();
        fetchPurchaseOrders();
      } else {
        alert(data.detail || 'Failed to create PO');
      }
    } catch (error) {
      console.error('Error creating PO:', error);
      alert('Error creating PO');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePO = async (poId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-purchase/pos/${poId}/approve`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      if (response.ok) {
        alert('PO Approved');
        fetchPurchaseOrders();
      }
    } catch (error) {
      console.error('Error approving PO:', error);
    }
  };

  const handleRecordPayment = async () => {
    if (!payment.amount || payment.amount <= 0) {
      alert('Please enter valid payment amount');
      return;
    }

    // Validate based on payment mode
    if (payment.payment_mode === 'UPI' && !payment.transaction_id) {
      alert('UPI Transaction ID is required');
      return;
    }
    if (payment.payment_mode === 'Cash' && !payment.receipt_number) {
      alert('Cash Receipt Number is required');
      return;
    }
    if (payment.payment_mode === 'Bank_Transfer' && !payment.transaction_id) {
      alert('Bank Transaction ID is required');
      return;
    }
    if (payment.payment_mode === 'Cheque' && !payment.cheque_number) {
      alert('Cheque Number is required');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-purchase/pos/${selectedPO.po_id}/record-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(payment)
        }
      );
      const data = await response.json();
      
      if (response.ok) {
        alert(`Payment recorded. Status: ${data.payment_status}`);
        setShowPaymentModal(false);
        setPayment({
          payment_mode: 'UPI',
          amount: 0,
          transaction_id: '',
          receipt_number: '',
          cheque_number: '',
          bank_name: '',
          reference_notes: ''
        });
        fetchPurchaseOrders();
        fetchDashboard();
      } else {
        alert(data.detail || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Draft': { color: 'bg-gray-100 text-gray-700', icon: FileText },
      'Pending_Approval': { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      'Approved': { color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
      'Sent_To_Supplier': { color: 'bg-purple-100 text-purple-700', icon: Truck },
      'Partially_Received': { color: 'bg-orange-100 text-orange-700', icon: Package },
      'Received': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'Cancelled': { color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig['Draft'];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const config = {
      'Pending': 'bg-red-100 text-red-700',
      'Partial': 'bg-yellow-100 text-yellow-700',
      'Paid': 'bg-green-100 text-green-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config[status] || config['Pending']}`}>
        {status}
      </span>
    );
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalGst = 0;
    newPO.items.forEach(item => {
      subtotal += item.unit_price * item.quantity;
      totalGst += item.gst_amount || 0;
    });
    return { subtotal, totalGst, grandTotal: subtotal + totalGst };
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Product Purchase</h1>
            <p className="text-gray-500">Purchase phones, laptops, tablets from suppliers</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] transition-colors"
            data-testid="create-po-btn"
          >
            <Plus className="w-5 h-5" />
            Create Purchase Order
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['dashboard', 'orders', 'grn'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-[#2b3380] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab === 'dashboard' && 'Dashboard'}
              {tab === 'orders' && 'Purchase Orders'}
              {tab === 'grn' && 'GRN / Receiving'}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total POs</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboard.total_pos}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Pending POs</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboard.pending_pos}</p>
                    <p className="text-xs text-gray-400">₹{dashboard.pending_value?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Package className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Received</p>
                    <p className="text-2xl font-bold text-gray-900">{dashboard.received_pos}</p>
                    <p className="text-xs text-gray-400">₹{dashboard.received_value?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <CreditCard className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Pending Payments</p>
                    <p className="text-2xl font-bold text-gray-900">₹{dashboard.pending_payments?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Suppliers */}
            {dashboard.top_suppliers?.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold mb-4">Top Suppliers</h3>
                <div className="space-y-3">
                  {dashboard.top_suppliers.map((supplier, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#2b3380] text-white rounded-full flex items-center justify-center text-sm font-medium">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{supplier.name}</p>
                          <p className="text-xs text-gray-500">{supplier.count} orders</p>
                        </div>
                      </div>
                      <p className="font-semibold text-gray-900">₹{supplier.total?.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Purchase Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500">Status:</span>
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1.5 border rounded-lg text-sm"
                >
                  <option value="all">All</option>
                  <option value="Draft">Draft</option>
                  <option value="Approved">Approved</option>
                  <option value="Sent_To_Supplier">Sent to Supplier</option>
                  <option value="Partially_Received">Partially Received</option>
                  <option value="Received">Received</option>
                </select>
                <button
                  onClick={fetchPurchaseOrders}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* PO List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">PO Number</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">Supplier</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">Bill Type</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">Items</th>
                    <th className="text-right p-4 text-sm font-medium text-gray-500">Amount</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">Payment</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrders.map(po => (
                    <tr key={po.po_id} className="border-b hover:bg-gray-50">
                      <td className="p-4">
                        <p className="font-medium text-gray-900">{po.po_number}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(po.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-gray-900">{po.supplier_name}</p>
                        {po.supplier_gst && (
                          <p className="text-xs text-gray-400">GST: {po.supplier_gst}</p>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          po.bill_type === 'GST_Invoice' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {po.bill_type === 'GST_Invoice' ? 'GST Invoice' : 'Challan'}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-gray-900">{po.items?.length || 0} items</p>
                      </td>
                      <td className="p-4 text-right">
                        <p className="font-semibold text-gray-900">₹{po.grand_total?.toLocaleString()}</p>
                        {po.total_gst > 0 && (
                          <p className="text-xs text-gray-400">GST: ₹{po.total_gst?.toLocaleString()}</p>
                        )}
                      </td>
                      <td className="p-4">{getStatusBadge(po.status)}</td>
                      <td className="p-4">{getPaymentStatusBadge(po.payment_status)}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {po.status === 'Draft' && (
                            <button
                              onClick={() => handleApprovePO(po.po_id)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Approve"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {po.payment_status !== 'Paid' && po.status !== 'Draft' && (
                            <button
                              onClick={() => {
                                setSelectedPO(po);
                                setPayment(prev => ({
                                  ...prev,
                                  amount: po.grand_total - (po.paid_amount || 0)
                                }));
                                setShowPaymentModal(true);
                              }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="Record Payment"
                            >
                              <CreditCard className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/product-purchase/${po.po_id}`)}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {purchaseOrders.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-gray-500">
                        No purchase orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* GRN Tab */}
        {activeTab === 'grn' && (
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">GRN / Goods Receiving</h3>
            <p className="text-gray-500 mb-4">Create GRN from approved purchase orders to receive goods</p>
            <button
              onClick={() => setActiveTab('orders')}
              className="px-4 py-2 bg-[#2b3380] text-white rounded-lg"
            >
              View Purchase Orders
            </button>
          </div>
        )}

        {/* Create PO Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
                <h2 className="text-lg font-semibold">Create Product Purchase Order</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Supplier & Bill Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                    <select
                      value={newPO.supplier_id}
                      onChange={(e) => setNewPO(prev => ({ ...prev, supplier_id: e.target.value }))}
                      className="w-full p-2 border rounded-lg"
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map(s => (
                        <option key={s.supplier_id} value={s.supplier_id}>
                          {s.company_name || s.name} {s.gst_number && `(${s.gst_number})`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bill Type *</label>
                    <select
                      value={newPO.bill_type}
                      onChange={(e) => setNewPO(prev => ({ ...prev, bill_type: e.target.value }))}
                      className="w-full p-2 border rounded-lg"
                    >
                      <option value="GST_Invoice">GST Invoice</option>
                      <option value="Challan">Challan (No GST)</option>
                    </select>
                  </div>
                </div>

                {/* Invoice Details */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Invoice #</label>
                    <input
                      type="text"
                      value={newPO.supplier_invoice_number}
                      onChange={(e) => setNewPO(prev => ({ ...prev, supplier_invoice_number: e.target.value }))}
                      className="w-full p-2 border rounded-lg"
                      placeholder="INV-001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                    <input
                      type="date"
                      value={newPO.supplier_invoice_date}
                      onChange={(e) => setNewPO(prev => ({ ...prev, supplier_invoice_date: e.target.value }))}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expected Delivery</label>
                    <input
                      type="date"
                      value={newPO.expected_delivery_date}
                      onChange={(e) => setNewPO(prev => ({ ...prev, expected_delivery_date: e.target.value }))}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                </div>

                {/* Add Items */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-medium mb-3">Add Products</h3>
                  <div className="grid grid-cols-5 gap-3 mb-3">
                    <div className="col-span-2">
                      <select
                        value={newItem.product_id}
                        onChange={(e) => handleProductSelect(e.target.value)}
                        className="w-full p-2 border rounded-lg text-sm"
                      >
                        <option value="">Select Product</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.brand_name} {p.model_name} ({p.sku_code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <input
                        type="number"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                        className="w-full p-2 border rounded-lg text-sm"
                        placeholder="Qty"
                        min="1"
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        value={newItem.unit_price}
                        onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                        className="w-full p-2 border rounded-lg text-sm"
                        placeholder="Unit Price"
                      />
                    </div>
                    <div>
                      <button
                        onClick={handleAddItem}
                        className="w-full p-2 bg-[#2b3380] text-white rounded-lg text-sm"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Items Table */}
                  {newPO.items.length > 0 && (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2">Product</th>
                          <th className="text-right p-2">Qty</th>
                          <th className="text-right p-2">Price</th>
                          <th className="text-right p-2">GST</th>
                          <th className="text-right p-2">Total</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {newPO.items.map((item, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="p-2">{item.product_name}</td>
                            <td className="text-right p-2">{item.quantity}</td>
                            <td className="text-right p-2">₹{item.unit_price?.toLocaleString()}</td>
                            <td className="text-right p-2">₹{item.gst_amount?.toLocaleString()}</td>
                            <td className="text-right p-2 font-medium">₹{item.total_amount?.toLocaleString()}</td>
                            <td className="p-2">
                              <button
                                onClick={() => handleRemoveItem(idx)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t bg-gray-50">
                        <tr>
                          <td colSpan={3} className="p-2 text-right font-medium">Subtotal:</td>
                          <td colSpan={2} className="p-2 text-right">₹{calculateTotals().subtotal.toLocaleString()}</td>
                          <td></td>
                        </tr>
                        {newPO.bill_type === 'GST_Invoice' && (
                          <tr>
                            <td colSpan={3} className="p-2 text-right font-medium">Total GST:</td>
                            <td colSpan={2} className="p-2 text-right">₹{calculateTotals().totalGst.toLocaleString()}</td>
                            <td></td>
                          </tr>
                        )}
                        <tr>
                          <td colSpan={3} className="p-2 text-right font-bold">Grand Total:</td>
                          <td colSpan={2} className="p-2 text-right font-bold text-lg">₹{calculateTotals().grandTotal.toLocaleString()}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={newPO.notes}
                    onChange={(e) => setNewPO(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                    rows={2}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 p-4 border-t flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePO}
                  disabled={loading || !newPO.supplier_id || newPO.items.length === 0}
                  className="px-6 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create PO'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedPO && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-md">
              <div className="p-4 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold">Record Payment</h2>
                  <p className="text-sm text-gray-500">{selectedPO.po_number}</p>
                </div>
                <button onClick={() => setShowPaymentModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Total Amount:</span>
                    <span className="font-medium">₹{selectedPO.grand_total?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Paid:</span>
                    <span className="text-green-600">₹{selectedPO.paid_amount?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium border-t mt-2 pt-2">
                    <span>Pending:</span>
                    <span className="text-red-600">₹{(selectedPO.grand_total - (selectedPO.paid_amount || 0)).toLocaleString()}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode *</label>
                  <select
                    value={payment.payment_mode}
                    onChange={(e) => setPayment(prev => ({ ...prev, payment_mode: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="UPI">UPI</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank_Transfer">Bank Transfer</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Credit">Supplier Credit</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    value={payment.amount}
                    onChange={(e) => setPayment(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                    className="w-full p-2 border rounded-lg"
                  />
                </div>

                {/* Conditional POP fields */}
                {payment.payment_mode === 'UPI' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">UPI Transaction ID *</label>
                    <input
                      type="text"
                      value={payment.transaction_id}
                      onChange={(e) => setPayment(prev => ({ ...prev, transaction_id: e.target.value }))}
                      className="w-full p-2 border rounded-lg"
                      placeholder="Enter UPI Transaction ID"
                    />
                  </div>
                )}

                {payment.payment_mode === 'Cash' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Number *</label>
                    <input
                      type="text"
                      value={payment.receipt_number}
                      onChange={(e) => setPayment(prev => ({ ...prev, receipt_number: e.target.value }))}
                      className="w-full p-2 border rounded-lg"
                      placeholder="Enter Cash Receipt Number"
                    />
                  </div>
                )}

                {payment.payment_mode === 'Bank_Transfer' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID *</label>
                      <input
                        type="text"
                        value={payment.transaction_id}
                        onChange={(e) => setPayment(prev => ({ ...prev, transaction_id: e.target.value }))}
                        className="w-full p-2 border rounded-lg"
                        placeholder="Enter Bank Transaction ID"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                      <input
                        type="text"
                        value={payment.bank_name}
                        onChange={(e) => setPayment(prev => ({ ...prev, bank_name: e.target.value }))}
                        className="w-full p-2 border rounded-lg"
                        placeholder="Enter Bank Name"
                      />
                    </div>
                  </>
                )}

                {payment.payment_mode === 'Cheque' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cheque Number *</label>
                      <input
                        type="text"
                        value={payment.cheque_number}
                        onChange={(e) => setPayment(prev => ({ ...prev, cheque_number: e.target.value }))}
                        className="w-full p-2 border rounded-lg"
                        placeholder="Enter Cheque Number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                      <input
                        type="text"
                        value={payment.bank_name}
                        onChange={(e) => setPayment(prev => ({ ...prev, bank_name: e.target.value }))}
                        className="w-full p-2 border rounded-lg"
                        placeholder="Enter Bank Name"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference Notes</label>
                  <textarea
                    value={payment.reference_notes}
                    onChange={(e) => setPayment(prev => ({ ...prev, reference_notes: e.target.value }))}
                    className="w-full p-2 border rounded-lg"
                    rows={2}
                    placeholder="Additional payment notes..."
                  />
                </div>
              </div>

              <div className="p-4 border-t flex justify-end gap-3">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordPayment}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductPurchase;
